import React, { useEffect, useMemo, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { CacheProvider } from '@emotion/react';
import createCache from '@emotion/cache';
import { prefixer } from 'stylis';
import rtlPlugin from 'stylis-plugin-rtl';
import i18n, { dirFor } from './i18n';
import { buildTheme } from './theme';
import { AuthProvider } from './contexts/AuthContext';
import { RuntimeConfigProvider } from './contexts/RuntimeConfigContext';
import Login from './components/Login';
import Signup from './components/Signup';
import PrivateRoute from './components/PrivateRoute';
import DashboardRouter from './components/DashboardRouter';
import PaypalCallback from './components/PaypalCallback';
import WebVersionGate from './components/WebVersionGate';
import ClaimBusinessPage from './components/ClaimBusinessPage';

// One cache per direction: the rtl plugin must not run when English is active.
const rtlCache = createCache({ key: 'mui-rtl', stylisPlugins: [prefixer, rtlPlugin] });
const ltrCache = createCache({ key: 'mui-ltr', stylisPlugins: [prefixer] });

function App() {
  const [lang, setLang] = useState(i18n.language);

  useEffect(() => {
    const handler = (nextLang) => setLang(nextLang);
    i18n.on('languageChanged', handler);
    return () => i18n.off('languageChanged', handler);
  }, []);

  const direction = dirFor(lang);
  const theme = useMemo(() => buildTheme(direction), [direction]);

  return (
    <CacheProvider value={direction === 'rtl' ? rtlCache : ltrCache}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
      <AuthProvider>
      <RuntimeConfigProvider>
        <WebVersionGate />
        <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/signup" element={<Signup />} />
            <Route path="/claim/:providerId" element={<ClaimBusinessPage />} />
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <DashboardRouter />
                </PrivateRoute>
              }
            />
            <Route
              path="/paypal-callback"
              element={
                <PrivateRoute>
                  <PaypalCallback />
                </PrivateRoute>
              }
            />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
            <Route path="*" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Router>
      </RuntimeConfigProvider>
      </AuthProvider>
      </ThemeProvider>
    </CacheProvider>
  );
}

export default App;
